import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApparelOrder, Invoice } from "@/types/models";
import { formatCurrency } from "@/lib/format";
import { getStatusColor } from '../utils/statusColors';
import ApparelService from "@/services/apparelService";
import { toast } from "sonner";

interface OrderDetailsDialogProps {
  order: ApparelOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoices: Invoice[];
  onUpdateStatus: (orderId: string, status: string) => Promise<void>;
  onOrderUpdated?: () => void; // Callback when order is updated (like added to invoice)
}

const OrderDetailsDialog: React.FC<OrderDetailsDialogProps> = ({
  order,
  open,
  onOpenChange,
  invoices,
  onUpdateStatus,
  onOrderUpdated,
}) => {
  // Add local state for these variables since they're passed to the parent component
  const [selectedInvoice, setSelectedInvoice] = useState<string>("");
  const [isAddingToInvoice, setIsAddingToInvoice] = useState(false);

  const handleAddToInvoice = async () => {
    if (!order || !selectedInvoice) {
      toast.error("Please select an invoice");
      return;
    }

    setIsAddingToInvoice(true);
    try {
      const success = await ApparelService.addOrderToInvoice(order.id, selectedInvoice);
      if (success) {
        toast.success("Order added to invoice");
        if (onOrderUpdated) {
          onOrderUpdated();
        }
      } else {
        toast.error("Failed to add order to invoice");
      }
    } catch (error) {
      console.error("Error adding order to invoice:", error);
      toast.error("Failed to add order to invoice");
    } finally {
      setIsAddingToInvoice(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Order Details</DialogTitle>
          <DialogDescription>
            Order #{order.id.substring(0, 8)}...
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-lg font-medium mb-2">Customer Information</h3>
              <p><strong>Family:</strong> {order.family?.name}</p>
              {order.student && (
                <p><strong>Student:</strong> {order.student.first_name} {order.student.last_name}</p>
              )}
              <p><strong>Created:</strong> {new Date(order.created_at).toLocaleString()}</p>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-2">Order Status</h3>
              <div className="flex items-center gap-2 mb-4">
                <Badge className={getStatusColor(order.status)}>
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </Badge>

                {!order.added_to_invoice && (
                  <Select 
                    value={order.status} 
                    onValueChange={(status) => onUpdateStatus(order.id, status)}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Update status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="ready">Ready for Pickup</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              {order.added_to_invoice ? (
                <div className="flex items-center mt-4">
                  <Badge variant="outline" className="bg-green-50">
                    Added to Invoice: {order.invoice_id?.substring(0, 8)}...
                  </Badge>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-4">
                  <Select value={selectedInvoice} onValueChange={setSelectedInvoice}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select invoice" />
                    </SelectTrigger>
                    <SelectContent>
                      {invoices.map((invoice) => (
                        <SelectItem key={invoice.id} value={invoice.id}>
                          Invoice #{invoice.id.substring(0, 8)}...
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={handleAddToInvoice} 
                    disabled={!selectedInvoice || isAddingToInvoice}
                  >
                    {isAddingToInvoice ? "Adding..." : "Add to Invoice"}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {order.notes && (
            <div>
              <h3 className="text-lg font-medium mb-2">Order Notes</h3>
              <p className="text-muted-foreground">{order.notes}</p>
            </div>
          )}

          <div>
            <h3 className="text-lg font-medium mb-2">Order Items</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {order.items?.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.product?.name}</TableCell>
                    <TableCell>{item.size || "N/A"}</TableCell>
                    <TableCell>{formatCurrency(item.price_cents / 100)}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency((item.price_cents * item.quantity) / 100)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex justify-end mt-4">
              <p className="font-semibold text-lg">
                Total: {formatCurrency(order.total_cents / 100)}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailsDialog;
